import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

// Local User interface to avoid import issues
interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  avatar?: string;
  company?: string;
  jobTitle?: string;
  isActive: boolean;
  isPrimarySuperAdmin?: boolean;
  permissions?: {
    dashboard?: boolean;
    studentRegistration?: boolean;
    focalPersons?: boolean;
    serviceCategories?: boolean;
    users?: boolean;
    jobSeekers?: boolean;
    userAnalytics?: boolean;
    jobs?: boolean;
    applications?: boolean;
    recruitment?: boolean;
    teachingJobs?: boolean;
    companyProfiles?: boolean;
    courses?: boolean;
    psychometricTests?: boolean;
    smartTests?: boolean;
    aiInterviews?: boolean;
    pastPapers?: boolean;
    certificates?: boolean;
    videos?: boolean;
    blogPosts?: boolean;
    systemAnalytics?: boolean;
    systemSettings?: boolean;
    systemHealth?: boolean;
    emailEvents?: boolean;
    teacherAttendance?: boolean;
    performance?: boolean;
    usageStats?: boolean;
    superAdminManagement?: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

// Local UserRole enum to avoid import issues
export enum UserRole {
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  TEACHER = 'teacher',
  STUDENT = 'student',
  PROFESSIONAL = 'professional',
  EMPLOYER = 'employer',
  JOB_SEEKER = 'job_seeker',
  FOCAL_PERSON = 'focal_person'
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ requiresOTP?: boolean; email?: string }>;
  verifyOTP: (email: string, otp: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasPermission: (permission: keyof User['permissions']) => boolean;
  isPrimarySuperAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Temporarily disable auth initialization to test basic functionality
    const initializeAuth = async () => {
      try {
        const { default: authService } = await import('../services/authService');
        const currentUser = authService.getCurrentUser();
        if (currentUser && authService.isAuthenticated()) {
          // Use cached user data without API call
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        // Clear invalid auth data
        const { default: authService } = await import('../services/authService');
        authService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ requiresOTP?: boolean; email?: string }> => {
    try {
      setIsLoading(true);
      // Ensure we're sending the correct data structure
      const credentials = { 
        email: email, 
        password: password 
      };
      console.log('AuthContext: Preparing login with credentials:', {
        email: credentials.email,
        password: credentials.password ? '***' : '(empty)'
      });
      const { default: authService } = await import('../services/authService');
      const authData = await authService.login(credentials);

      // OTP/2FA disabled for super admin logins for now
      setUser(authData.user);
      return {};
    } catch (error) {
      console.error('AuthContext login error:', error);
      setUser(null); // Ensure user is cleared on login failure
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOTP = async (email: string, otp: string): Promise<void> => {
    try {
      setIsLoading(true);
      const { default: superAdminService } = await import('../services/superAdminService');
      const result = await superAdminService.verifyOTP(email, otp);
      
      // Store token and user data
      localStorage.setItem('token', result.token);
      localStorage.setItem('user', JSON.stringify(result.user));
      
      // Update user state
      setUser(result.user);
    } catch (error) {
      console.error('OTP verification error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: any): Promise<void> => {
    try {
      setIsLoading(true);
      const { default: authService } = await import('../services/authService');
      const authData = await authService.register(userData);
      setUser(authData.user);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    const { default: authService } = await import('../services/authService');
    authService.logout();
    setUser(null);
  };

  const updateUser = async (userData: Partial<User>): Promise<void> => {
    try {
      const { default: authService } = await import('../services/authService');
      const updatedUser = await authService.updateProfile(userData);
      setUser(updatedUser);
    } catch (error) {
      throw error;
    }
  };

  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role as UserRole) : false;
  };

  const hasPermission = (permission: keyof User['permissions']): boolean => {
    // Primary super admin has all permissions
    if (user?.isPrimarySuperAdmin) return true;
    // Super admin with specific permissions
    if (user?.role === UserRole.SUPER_ADMIN) {
      return user?.permissions?.[permission] ?? true;
    }
    // Other roles have all permissions by default
    return true;
  };

  const isPrimarySuperAdmin = (): boolean => {
    return user?.isPrimarySuperAdmin || false;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    verifyOTP,
    register,
    logout,
    updateUser,
    hasRole,
    hasAnyRole,
    hasPermission,
    isPrimarySuperAdmin
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;